import { useIsCountrySpecificEhrSelectionEnabled } from '@/features/flags';
import {
  integrationMapping,
  integrationMappingByUniqueId,
} from '@/helpers/integrationMapping';
import type { IntegrationProviderOption } from '@heidi/integrations/ehr/contracts';
import { useQueryIntegrationProviders } from '@heidi/integrations/ehr/data';
import type { EHRProvider } from '@heidi/schema/ml-scribe';

/** An {@link IntegrationProviderOption} plus its Heidi icon, when one exists. */
export type SelectedEmrOption = IntegrationProviderOption & { icon?: string };

/**
 * Resolves the selected EMR/EHR (`emrEhr` holds the display name in both
 * flag states) to an id + name + icon triple. Reads the same cache entry as
 * ClinicStep's option fetch, so no extra request.
 */
export function useSelectedEmrOption(
  emrEhr: string | null | undefined,
  countryCode: string | undefined
): SelectedEmrOption | undefined {
  const isCountryScopedEhrList = useIsCountrySpecificEhrSelectionEnabled();
  const providersQuery = useQueryIntegrationProviders({
    country: countryCode,
    enabled: isCountryScopedEhrList && !!countryCode && !!emrEhr,
  });

  if (!emrEhr) return undefined;
  if (isCountryScopedEhrList) {
    const match = providersQuery.data?.find((provider) => provider.name === emrEhr);
    return {
      uniqueId: match?.uniqueId ?? emrEhr,
      name: emrEhr,
      icon:
        integrationMapping[emrEhr as EHRProvider]?.icon ||
        (match ? integrationMappingByUniqueId[match.uniqueId]?.icon : undefined),
    };
  }
  const legacy = integrationMapping[emrEhr as EHRProvider];
  return {
    uniqueId: emrEhr,
    name: legacy?.name || emrEhr,
    icon: legacy?.icon || undefined,
  };
}
